/**
 * ShortcutsOverlay — centered help modal listing all Mission Control
 * keyboard shortcuts.
 *
 * Props: { open, onClose }
 * Closes on: Escape, backdrop click, or pressing ? again
 * (the ? re-toggle is handled in useShortcuts → onShortcutsOverlay,
 *  which calls onClose when already open — this component simply
 *  respects open/onClose).
 */
import { useEffect } from "react";

// ── Data ─────────────────────────────────────────────────────────────────────

const SECTIONS = [
  {
    title: "NAVIGATION",
    rows: [
      { keys: ["g", "n"], label: "Now room"       },
      { keys: ["g", "d"], label: "Decisions room"  },
      { keys: ["g", "a"], label: "Agents room"     },
      { keys: ["g", "p"], label: "People room"     },
      { keys: ["g", "m"], label: "Money room"      },
      { keys: ["g", "c"], label: "Compass room"    },
      { keys: ["g", "l"], label: "Legal room"      },
    ],
  },
  {
    title: "ACTIONS",
    rows: [
      { keys: ["⌘", "K"],      label: "Search (command palette)"   },
      { keys: ["⌘", "."],      label: "Toggle cockpit / paper mode" },
      { keys: ["⌘", "⇧", "!"], label: "I am driving (pause all agents)" },
      { keys: ["?"],           label: "Show this help"              },
      { keys: ["Esc"],         label: "Close drawer / panel"        },
    ],
  },
  {
    title: "DECISIONS ROOM",
    rows: [
      { keys: ["j", "↓"], label: "Focus next decision"         },
      { keys: ["k", "↑"], label: "Focus previous decision"     },
      { keys: ["a"],      label: "Approve focused decision"    },
      { keys: ["r"],      label: "Reject focused decision"     },
      { keys: ["s"],      label: "Snooze focused decision (1h)" },
    ],
  },
];

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
  backdrop: {
    position:        "fixed",
    inset:           0,
    backgroundColor: "rgba(0,0,0,0.5)",
    zIndex:          200,
    display:         "flex",
    alignItems:      "center",
    justifyContent:  "center",
  },
  modal: {
    width:           "100%",
    maxWidth:        480,
    backgroundColor: "var(--mc-bg)",
    border:          "1px solid var(--mc-border)",
    borderRadius:    8,
    overflow:        "hidden",
    boxShadow:       "0 24px 80px -30px rgba(14,20,16,0.45)",
  },
  header: {
    height:          44,
    display:         "flex",
    alignItems:      "center",
    padding:         "0 20px",
    borderBottom:    "1px solid var(--mc-border)",
    flexShrink:      0,
  },
  headerTitle: {
    flex:            1,
    fontFamily:      "var(--font-display)",
    fontSize:        16,
    fontWeight:      400,
    color:           "var(--mc-text)",
    margin:          0,
  },
  closeBtn: {
    background:      "transparent",
    border:          "none",
    cursor:          "pointer",
    color:           "var(--color-slate-400)",
    fontFamily:      "var(--font-sans)",
    fontSize:        18,
    lineHeight:      1,
    padding:         "0 0 0 12px",
    display:         "flex",
    alignItems:      "center",
    justifyContent:  "center",
    transition:      "color 120ms",
  },
  sectionHeader: (isFirst) => ({
    fontFamily:      "var(--font-mono)",
    fontSize:        10,
    textTransform:   "uppercase",
    letterSpacing:   "0.1em",
    color:           "var(--color-slate-400)",
    padding:         "10px 20px 6px",
    borderTop:       isFirst ? "none" : "1px solid var(--mc-border)",
  }),
  shortcutRow: {
    display:         "flex",
    flexDirection:   "row",
    alignItems:      "center",
    padding:         "6px 20px",
    gap:             12,
  },
  keysWrap: {
    display:         "flex",
    flexDirection:   "row",
    alignItems:      "center",
    gap:             4,
    minWidth:        80,
    flexShrink:      0,
  },
  kbd: {
    fontFamily:      "var(--font-mono)",
    fontSize:        12,
    backgroundColor: "var(--mc-surface)",
    border:          "1px solid var(--mc-border)",
    borderRadius:    4,
    padding:         "2px 6px",
    minWidth:        24,
    textAlign:       "center",
    color:           "var(--mc-text)",
    lineHeight:      1.5,
    display:         "inline-block",
  },
  kbdSeparator: {
    fontFamily:      "var(--font-mono)",
    fontSize:        10,
    color:           "var(--color-slate-600)",
    userSelect:      "none",
  },
  rowLabel: {
    fontFamily:      "var(--font-sans)",
    fontSize:        13,
    color:           "var(--mc-text)",
    flex:            1,
  },
};

// ── KeySequence — renders one or more <kbd> chips for a shortcut ─────────────

function KeySequence({ keys }) {
  return (
    <span style={S.keysWrap}>
      {keys.map((k, i) => (
        <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
          {i > 0 && (
            <span style={S.kbdSeparator} aria-hidden="true"> </span>
          )}
          <kbd style={S.kbd}>{k}</kbd>
        </span>
      ))}
    </span>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function ShortcutsOverlay({ open, onClose }) {
  // Close on Escape
  useEffect(() => {
    if (!open) return;

    function handler(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose?.();
      }
    }

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      style={S.backdrop}
      onClick={onClose}
      aria-modal="true"
      role="dialog"
      aria-label="Keyboard shortcuts"
    >
      <div
        style={S.modal}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ── */}
        <div style={S.header}>
          <h2 style={S.headerTitle}>Keyboard Shortcuts</h2>
          <button
            style={S.closeBtn}
            onClick={onClose}
            aria-label="Close shortcuts overlay"
            title="Close"
          >
            ×
          </button>
        </div>

        {/* ── Sections ── */}
        {SECTIONS.map((section, si) => (
          <div key={section.title}>
            <div style={S.sectionHeader(si === 0)} aria-hidden="true">
              {section.title}
            </div>

            {section.rows.map((row, ri) => (
              <div key={ri} style={S.shortcutRow}>
                <KeySequence keys={row.keys} />
                <span style={S.rowLabel}>{row.label}</span>
              </div>
            ))}
          </div>
        ))}

        {/* Bottom padding */}
        <div style={{ height: 12 }} />
      </div>
    </div>
  );
}
